<template>
  <div id="knowledge-base">
    <v-row>
      <v-col cols="12" md="3" lg="3" order="1" order-md="2" class="knowledge-base-summary-overlay">
        <v-card class="knowledge-base-summary" style="overflow-y: hidden">
          <div class="kb-questions py-1">
            <div
              v-for="subTitle in summary"
              :key="subTitle.title"
              class="kb-question d-flex align-start text-sm px-5 my-4 cursor-pointer"
            >
              <v-icon size="14" class="me-2 mt-1" color="secondary">
                {{ icons.mdiCheckboxBlankCircleOutline }}
              </v-icon>
              <span class="text--secondary"
                    @click="scrollToElement(subTitle.link)">{{ subTitle.title }}</span>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="9" lg="9" order="1" order-md="2">
        <v-card>
          <v-card-text>
            <h2>Merchanto Prevent API Guide</h2>
          </v-card-text>
          <v-card-title id="overview">1. Overview</v-card-title>
          <v-card-text>
            The <b>Merchanto Prevent API</b> enables merchants to participate in global dispute-prevention programs such as <b>Visa Order Insight (OI)</b> and <b>Mastercard Clarity</b>.
            Through these programs, issuers (banks) can retrieve transaction details directly from merchants in real-time when cardholders raise a question about a purchase.
          </v-card-text>
          <v-card-text>
            By integrating with the Prevent API, merchants can:
            <br/>
            <ul>
              <li>Share order and transaction details instantly with card issuers.</li>
              <li>Lower their fraud and dispute rate by providing trusted order information upfront, reducing false fraud claims.</li>
              <li>Reduce unnecessary disputes and chargebacks.</li>
              <li>Improve customer satisfaction by giving cardholders clear information about their purchases.</li>
              <li>Receive consistent notifications about dispute status changes via the Merchanto platform.
              </li>
            </ul>
          </v-card-text>
          <v-card-text>
            This guide provides everything your technical team needs to <b>integrate with the Prevent API</b> and make your data available to Visa, Mastercard, and issuers.
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="how-it-works">2. How It Works</v-card-title>
          <v-card-text>
            When a cardholder questions a transaction (via their <b>bank’s mobile app</b>, web portal, or call center):
          </v-card-text>
          <v-card-text>
            <ol>
              <li>The <b>issuer</b> requests details about the transaction from Visa/Mastercard.</li>
              <li>Visa/Mastercard check if the merchant is enrolled in <b>Order Insight/Clarity</b>.</li>
              <li>If enrolled, they forward the request to Merchanto.</li>
              <li>Merchanto sends the request (a “lookup”) to your system through the <b>Prevent API</b>.</li>
              <li>Your system responds in real-time with transaction details (order amount, date, product description, contact info, etc.).</li>
              <li>Merchanto formats the response and passes it back to Visa/Mastercard for display to the cardholder.</li>
            </ol>
          </v-card-text>
          <v-card-text>
            This immediate data exchange can prevent a dispute from escalating into a chargeback.
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="operation-flow">3. Operation flow</v-card-title>
          <v-card-text>
            Here’s the lifecycle of a typical lookup request:
          </v-card-text>
          <v-card-text>
            <ol>
              <li><b>Cardholder inquiry</b> &mdash; A cardholder requests more information about a purchase in their bank’s app or website.</li>
              <li><b>Issuer request</b> &mdash; The issuer asks Visa/Mastercard for details about the transaction.</li>
              <li><b>Scheme validation</b> &mdash; Visa/Mastercard confirm merchant enrollment and send a lookup request via Merchanto Prevent API.</li>
              <li><b>Merchant response</b> &mdash; Your system searches its database for the transaction and returns details in the API response.</li>
              <li><b>Merchanto processing</b> &mdash; Our platform validates and stores the response, then sends it back to Visa/Mastercard.</li>
              <li><b>Issuer decision</b> &mdash; Based on your response, the issuer may:
                <ul>
                  <li>Show the transaction details to the cardholder (preventing escalation).</li>
                  <li>Continue to a formal dispute/chargeback if insufficient details were provided.</li>
                  <li>Submit additional lookup requests (including for historical transactions).</li>
                </ul>
              </li>
            </ol>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="prevent-menu">4. Merchanto Platform: Prevent Menu</v-card-title>
          <v-card-text>
            Once integrated, the Merchanto dashboard provides visibility into the entire Prevent flow. The <b>Prevent</b> menu has three main sections:
          </v-card-text>
          <v-card-title class="subtitle-1" id="transactions">4.1. Transactions</v-card-title>
          <v-card-text>
            <ul>
              <li>Represent <b>cases</b> created when a lookup is received.</li>
              <li>Multiple lookups for the same cardholder inquiry are grouped under a single transaction.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title class="subtitle-1" id="orders">4.2. Orders (Lookups)</v-card-title>
          <v-card-text>
            <ul>
              <li>Represent the <b>individual requests</b> from Visa/Mastercard.</li>
              <li>Every order is linked to a transaction.</li>
            </ul>
            <br/>
            <b>Order statuses:</b>
            <ul>
              <li><b>Accepted</b> &mdash; Valid response returned within the allowed timeframe.</li>
              <li><b>Not Found</b> &mdash; Your system could not match the transaction.</li>
              <li><b>Invalid</b> &mdash; Response was returned but failed validation (not sent to Issuer).</li>
              <li><b>Timeout</b> &mdash; No response received within the allowed time window.</li>
              <li><b>Failed</b> &mdash; No response received at all.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title class="subtitle-1" id="notification">4.3. Notifications</v-card-title>
          <v-card-text>
            <ul>
              <li>Generated when a <b>transaction status changes</b>.</li>
            </ul>
            <br/>
            <b>Notification statuses</b> (aligned with Visa’s terminology):
            <ul>
              <li><b>New</b> &mdash; The case was deflected successfully.</li>
              <li><b>Failed</b> &mdash; The case escalated to dispute (chargeback).</li>
              <li><b>Deleted</b> &mdash; A previously deflected case has been reopened or chargeback received.</li>
            </ul>
          </v-card-text>
          <v-card-text>
            Note: These statuses were initially suggested by Visa and may not always reflect merchant expectations literally, but they consistently map to the transaction lifecycle described above.
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="benefits">5. Benefits of Using Prevent API</v-card-title>
          <v-card-text>
            <ul>
              <li><b>Dispute Prevention</b>: Provide order details to issuers in real-time, preventing many disputes from turning into chargebacks.</li>
              <li><b>Lower Fraud Rate</b>: By supplying trusted purchase information early, merchants reduce the number of false fraud claims, lowering their overall fraud ratio. This helps with compliance under Visa/Mastercard monitoring programs.</li>
              <li><b>Operational Efficiency</b>: While Visa and Mastercard require <b>separate integrations and webhooks</b>, the Merchanto platform provides a unified dashboard to monitor both.</li>
              <li><b>Real-time Data Exchange</b>: Immediate responses increase cardholder confidence and satisfaction.</li>
              <li><b>Visibility & Control</b>: All lookups, responses, and outcomes are stored and visible in the Merchanto platform.</li>
              <li><b>Automation</b>: Webhook notifications keep merchant systems synchronized without manual intervention.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="next-steps">6. Next Steps</v-card-title>
          <v-card-text>
            <ul>
              <li>Review the API Reference in next sections.</li>
              <li>Prepare your system to:
                <ol>
                  <li>Receive lookup requests.</li>
                  <li>Respond with transaction details.</li>
                  <li>Handle webhook notifications.</li>
                </ol>
              </li>
              <li>Test your integration in the Merchanto sandbox environment before going live.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import {
  mdiCogOutline,
  mdiCheckboxBlankCircleOutline,
  mdiCellphoneCog,
  mdiChevronLeft,
} from '@mdi/js';
import useDocs from '@/views/docs/useDocs';

export default {
  setup() {
    const { scrollToElement } = useDocs();

    const summary = [
      { title: '1. Overview', link: 'overview' },
      { title: '2. How It Works', link: 'how-it-works' },
      { title: '3. Operation flow', link: 'operation-flow' },
      { title: '4. Merchanto Platform: Prevent Menu', link: 'prevent-menu' },
      { title: '4.1. Transactions', link: 'transactions' },
      { title: '4.2. Orders (Lookups)', link: 'orders' },
      { title: '4.3. Notifications', link: 'notification' },
      { title: '5. Benefits of Using Prevent API', link: 'benefits' },
      { title: '6. Next Steps', link: 'next-steps' },
    ];

    return {
      summary,
      icons: {
        mdiCogOutline,
        mdiCheckboxBlankCircleOutline,
        mdiCellphoneCog,
        mdiChevronLeft,
      },
      scrollToElement,
    };
  },
};
</script>

<style lang="scss">
@import '@core/preset/preset/pages/knowledge-base';
</style>
